import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { type QueryClient, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import type { AllCacheKeys } from "./cacheKeys";

import { getTechnicalConfigToastError } from "../tableHeaders";
import { invalidateConfigGroupQueries } from "./invalidation";

type MutationError = { response?: { data?: unknown } };

type OptimisticGroupMutationParams<TGroup, TVariables> = {
  applyOptimisticPatch?: (group: TGroup, variables: TVariables) => TGroup;
  cacheKeys: AllCacheKeys;
  getProjectId: (variables: TVariables) => string;
  getRecordId: (variables: TVariables) => string | undefined;
  getScenario?: (variables: TVariables) => string | undefined;
  matchesGroup?: (group: TGroup, variables: TVariables) => boolean;
  queryClient: QueryClient;
  successMessageKey?: string;
};

export const buildOptimisticGroupMutationOptions = <TGroup, TVariables>({
  applyOptimisticPatch,
  cacheKeys,
  getProjectId,
  getRecordId,
  getScenario,
  matchesGroup,
  queryClient,
  successMessageKey,
}: OptimisticGroupMutationParams<TGroup, TVariables>) => ({
  onError: (error: MutationError) => {
    toast.error(getTechnicalConfigToastError(error));
  },
  onMutate: async (variables: TVariables) => {
    if (!applyOptimisticPatch || !matchesGroup) return { previousGroups: undefined };

    const groupsKey = cacheKeys.groups(getProjectId(variables), getScenario?.(variables));
    await queryClient.cancelQueries(groupsKey);

    const previousGroups = queryClient.getQueryData<TGroup[]>(groupsKey);

    queryClient.setQueryData<TGroup[]>(groupsKey, (groups) =>
      (groups ?? []).map((group) => (matchesGroup(group, variables) ? applyOptimisticPatch(group, variables) : group))
    );

    return { previousGroups };
  },
  onSettled: (_result: unknown, _error: unknown, variables: TVariables) => {
    invalidateConfigGroupQueries(queryClient, cacheKeys, getProjectId(variables), getRecordId(variables));
  },
  onSuccess: () => {
    if (successMessageKey) {
      toast.success(String(i18next.t(successMessageKey)));
    }
  },
});

type ConfigCatalogMutationParams<TPayload> = {
  invalidateCatalog: (queryClient: QueryClient) => void;
  mutationFn: (variables: { data: TPayload }) => Promise<unknown>;
  successMessageKey: string;
};

export const useConfigCatalogMutation = <TPayload>({
  invalidateCatalog,
  mutationFn,
  successMessageKey,
}: ConfigCatalogMutationParams<TPayload>) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation(mutationFn, {
    onError: (error: MutationError) => {
      toast.error(getTechnicalConfigToastError(error));
    },
    onSettled: () => {
      invalidateCatalog(queryClient);
    },
    onSuccess: () => {
      toast.success(String(t(successMessageKey)));
    },
  });
};
